import axios from 'axios'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { NotificationSettingListResponse } from 'src/models/notificationSetting.models'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { PatchNotificationSettingsRequest } from 'src/requests/patchNotificationSettings.request'

export const GET_NOTIFICATION_SETTINGS_INIT =
  'data/notificationSettings/GET_NOTIFICATION_SETTINGS_INIT'
export const GET_NOTIFICATION_SETTINGS_SUCCESS =
  'data/notificationSettings/GET_NOTIFICATION_SETTINGS_SUCCESS'
export const GET_NOTIFICATION_SETTINGS_FAILURE =
  'data/notificationSettings/GET_NOTIFICATION_SETTINGS_FAILURE'

export interface GetNotificationSettingsSuccessPayload {
  notificationSettings: NotificationSettingListResponse[]
}

export interface GetNotificationSettingsInitAction {
  type: typeof GET_NOTIFICATION_SETTINGS_INIT
}
export interface GetNotificationSettingsSuccessAction {
  type: typeof GET_NOTIFICATION_SETTINGS_SUCCESS
  payload: GetNotificationSettingsSuccessPayload
}
export interface GetNotificationSettingsFailureAction {
  type: typeof GET_NOTIFICATION_SETTINGS_FAILURE
  payload: unknown
}

export type GetNotificationSettingsActions =
  | GetNotificationSettingsInitAction
  | GetNotificationSettingsSuccessAction
  | GetNotificationSettingsFailureAction

const createGetNotificationSettingsInitAction =
  (): GetNotificationSettingsInitAction => ({
    type: GET_NOTIFICATION_SETTINGS_INIT,
  })
const createGetNotificationSettingsSuccessAction = (
  payload: GetNotificationSettingsSuccessPayload,
): GetNotificationSettingsSuccessAction => ({
  type: GET_NOTIFICATION_SETTINGS_SUCCESS,
  payload,
})
const createGetNotificationSettingsFailureAction = (
  err: unknown,
): GetNotificationSettingsFailureAction => ({
  type: GET_NOTIFICATION_SETTINGS_FAILURE,
  payload: err,
})

export const getNotificationSettings = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetNotificationSettingsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetNotificationSettingsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetNotificationSettingsInitAction())
    return axios
      .get(
        `/notificationsettings`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createGetNotificationSettingsSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createGetNotificationSettingsFailureAction(err.response.data),
          )
        },
      )
  }
}

export const PATCH_NOTIFICATION_SETTINGS_INIT =
  'data/notificationSettings/PATCH_NOTIFICATION_SETTINGS_INIT'
export const PATCH_NOTIFICATION_SETTINGS_SUCCESS =
  'data/notificationSettings/PATCH_NOTIFICATION_SETTINGS_SUCCESS'
export const PATCH_NOTIFICATION_SETTINGS_FAILURE =
  'data/notificationSettings/PATCH_NOTIFICATION_SETTINGS_FAILURE'

export type PatchNotificationSettingsSuccessPayload = {
  notificationSettings: NotificationSettingListResponse[]
}

export interface PatchNotificationSettingsInitAction {
  type: typeof PATCH_NOTIFICATION_SETTINGS_INIT
}
export interface PatchNotificationSettingsSuccessAction {
  type: typeof PATCH_NOTIFICATION_SETTINGS_SUCCESS
  payload: PatchNotificationSettingsSuccessPayload
}
export interface PatchNotificationSettingsFailureAction {
  type: typeof PATCH_NOTIFICATION_SETTINGS_FAILURE
  payload: unknown
}

export type PatchNotificationSettingsActions =
  | PatchNotificationSettingsInitAction
  | PatchNotificationSettingsSuccessAction
  | PatchNotificationSettingsFailureAction

const createPatchNotificationSettingsInitAction =
  (): PatchNotificationSettingsInitAction => ({
    type: PATCH_NOTIFICATION_SETTINGS_INIT,
  })
const createPatchNotificationSettingsSuccessAction = (
  payload: PatchNotificationSettingsSuccessPayload,
): PatchNotificationSettingsSuccessAction => ({
  type: PATCH_NOTIFICATION_SETTINGS_SUCCESS,
  payload,
})
const createPatchNotificationSettingsFailureAction = (
  response: unknown,
): PatchNotificationSettingsFailureAction => ({
  type: PATCH_NOTIFICATION_SETTINGS_FAILURE,
  payload: response,
})

export const patchNotificationSettings = (
  body: Partial<PatchNotificationSettingsRequest>,
): ThunkAction<
  Promise<
    | PatchNotificationSettingsFailureAction
    | PatchNotificationSettingsSuccessAction
  >,
  RootState,
  void,
  PatchNotificationSettingsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PatchNotificationSettingsActions>,
    getState: () => RootState,
  ): Promise<
    | PatchNotificationSettingsFailureAction
    | PatchNotificationSettingsSuccessAction
  > => {
    dispatch(createPatchNotificationSettingsInitAction())
    return axios
      .patch<PatchNotificationSettingsSuccessPayload>(
        `/notificationsettings/`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createPatchNotificationSettingsSuccessAction(response.data),
          )
        },
        (err) => {
          return dispatch(
            createPatchNotificationSettingsFailureAction(err.response.data),
          )
        },
      )
  }
}
